import * as Sentry from '@sentry/react';
import type { Dispatch } from '@reduxjs/toolkit';

import { SET_TOAST_DURATION_IN_MILLISECONDS } from '../../../utils/setToastDuration';
import TCClient from 'client';
import { actions, store, thunk } from 'redux-stores';

const getOrganizationId = (): string =>
  store.getState().admin.selectedOrganizationId ||
  store.getState().session.currentOrganizationId ||
  '';

const getUserId = (): string => TCClient.currentUser?.id || '';

let fetchConfigSeq = 0;
let fetchBackupSeq = 0;

export const fetchServerConfig = async (dispatch: Dispatch) => {
  const organizationId = getOrganizationId();
  const userId = getUserId();

  if (!organizationId || !userId) {
    dispatch(actions.setServerConfigLoading(false));
    return;
  }

  const seq = ++fetchConfigSeq;
  dispatch(actions.setServerConfigLoading(true));

  try {
    const res = await TCClient.httpClient.get(`/v2/user/${userId}/organization`);

    if (seq !== fetchConfigSeq) return;

    const orgs = (res.data?.organizations || res.data || []) as Record<string, unknown>[];
    const raw = orgs.find((o) => o.token === organizationId);

    if (!raw) {
      Sentry.logger.error(
        Sentry.logger
          .fmt`[ServerConfig] Selected organization ${organizationId} not found in user's organizations`
      );
      dispatch(actions.setServerConfigLoading(false));
      thunk.setToastSettings({
        duration: SET_TOAST_DURATION_IN_MILLISECONDS(6),
        open: true,
        message: 'Unable to load configuration for the selected organization.',
        type: 'FAILURE',
      });
      return;
    }

    dispatch(
      actions.setServerConfig({
        standinBaseUrl: (raw.standin_base_url as string) || '',
        standinWifiSsids: (raw.standin_wifi_ssids as string) || '',
        isLocalServer: Boolean(raw.is_local_server),
      })
    );
  } catch (err) {
    if (seq !== fetchConfigSeq) return;
    Sentry.logger.error(Sentry.logger.fmt`[ServerConfig] Failed to fetch server config`, { err });
    dispatch(actions.setServerConfigLoading(false));
    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(6),
      open: true,
      message: 'Failed to load server configuration.',
      type: 'FAILURE',
    });
  }
};

export const saveServerConfig = async (dispatch: Dispatch) => {
  const { standinBaseUrl, standinWifiSsids } = store.getState().serverConfig;
  const organizationId = getOrganizationId();

  if (!organizationId) return;

  try {
    await TCClient.httpClient.post(`/v2/organization/${organizationId}/server-config`, {
      data: {
        standin_base_url: standinBaseUrl,
        standin_wifi_ssids: standinWifiSsids,
      },
    });

    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(3),
      open: true,
      message: 'Server configuration has been saved.',
      type: 'SUCCESS',
    });

    if (getOrganizationId() === organizationId) {
      await fetchServerConfig(dispatch);
    }
  } catch (err: unknown) {
    Sentry.logger.error(Sentry.logger.fmt`[ServerConfig] Failed to save server configuration`, {
      err,
    });
    const serverMessage = (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message;
    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(6),
      open: true,
      message: serverMessage || 'An error occurred when saving server configuration.',
      type: 'FAILURE',
    });
  }
};

export const fetchBackupRegistration = async (dispatch: Dispatch) => {
  const organizationId = getOrganizationId();

  if (!organizationId) return;

  const seq = ++fetchBackupSeq;

  try {
    const res = await TCClient.httpClient.get(
      `/v2/organization/${organizationId}/backup-registration`
    );

    if (seq !== fetchBackupSeq) return;

    dispatch(
      actions.setBackupRegistration({
        isStandinEnabled: true,
        standinTargetToken: res.data?.target_org_token || '',
      })
    );
  } catch (err: unknown) {
    if (seq !== fetchBackupSeq) return;

    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      dispatch(
        actions.setBackupRegistration({
          isStandinEnabled: false,
          standinTargetToken: '',
        })
      );
    } else {
      Sentry.logger.error(Sentry.logger.fmt`[ServerConfig] Failed to load backup registration`, {
        err,
      });
      thunk.setToastSettings({
        duration: SET_TOAST_DURATION_IN_MILLISECONDS(6),
        open: true,
        message: 'Failed to load standin backup status.',
        type: 'FAILURE',
      });
    }
  }
};

export const enableStandinBackup = async (dispatch: Dispatch) => {
  const organizationId = getOrganizationId();

  if (!organizationId) return;

  dispatch(actions.setBackupLoading(true));
  try {
    const res = await TCClient.httpClient.post(
      `/v2/organization/${organizationId}/backup-registration`,
      {
        data: {},
      }
    );
    dispatch(
      actions.setBackupRegistration({
        isStandinEnabled: true,
        standinTargetToken: res.data?.target_org_token || '',
      })
    );
    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(3),
      open: true,
      message: 'Standin backup has been enabled for this organization.',
      type: 'SUCCESS',
    });
  } catch {
    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(6),
      open: true,
      message: 'An error occurred when enabling standin backup.',
      type: 'FAILURE',
    });
  } finally {
    dispatch(actions.setBackupLoading(false));
  }
};

export const disableStandinBackup = async (dispatch: Dispatch) => {
  const organizationId = getOrganizationId();

  if (!organizationId) return;

  dispatch(actions.setBackupLoading(true));
  try {
    await TCClient.httpClient.del(`/v2/organization/${organizationId}/backup-registration`);
    dispatch(
      actions.setBackupRegistration({
        isStandinEnabled: false,
        standinTargetToken: '',
      })
    );
    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(3),
      open: true,
      message: 'Standin backup has been disabled for this organization.',
      type: 'SUCCESS',
    });
  } catch {
    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(6),
      open: true,
      message: 'An error occurred when disabling standin backup.',
      type: 'FAILURE',
    });
  } finally {
    dispatch(actions.setBackupLoading(false));
  }
};
